import { FC } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import moment from "moment";
import { IoLocationOutline } from "react-icons/io5";
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

interface QueryPropertyCardProps {
  property: any;
  bookingQuery: any;
  isSharedProperty: boolean;
  checkInDate: Date;
  checkOutDate: Date;
}

const QueryPropertyCard: FC<QueryPropertyCardProps> = ({
  property,
  bookingQuery,
  isSharedProperty,
  checkInDate,
  checkOutDate,
}) => {
  const sortAddress = `${property?.city || ""}, ${
    property?.state || ""
  }`.trim();
  const href = `/listing-stay-detail/${property?.slug}`;

  const minimumMonths = property?.minimum_months_of_booking;
  const bookedMonths = bookingQuery?.no_of_months;
  const isBookingTooShort = minimumMonths && bookedMonths < minimumMonths;

  const modifiedStatus = (PropertyStatus: string | undefined | boolean) => {
    const status =
      typeof PropertyStatus === "boolean"
        ? PropertyStatus.toString()
        : PropertyStatus;
    switch (status) {
      case "true":
        return "Shared Property";
      case "false":
        return "Entire Property";
      default:
        return "";
    }
  };
  

  return (
    <div className="w-full bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden shadow-lg border border-blue-200 dark:border-blue-800">
      <div className="p-6 space-y-4">
        {/* Property Gallery */}
        <div className="relative rounded-xl overflow-hidden">
          <GallerySlider
            uniqueID={`QueryPropertyCard_${property?.id}`}
            ratioClass="aspect-w-4 aspect-h-3"
            galleryImgs={property?.place_images || []}
            href={href}
          />
          <div className="absolute left-3 top-3 z-[1] bg-blue-600/90 px-4 py-1.5 rounded-full text-white text-sm font-medium">
            {modifiedStatus(isSharedProperty)}
          </div>
        </div>

        {/* Property Title & Location */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 line-clamp-2">
            {property?.title}
          </h2>
          <p className="flex gap-2 items-center text-gray-600 dark:text-gray-400">
            <IoLocationOutline className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{sortAddress}</span>
          </p>
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Property Details */}
        <div className="space-y-3">
          {isSharedProperty ? (
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <div className="flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Booking For Room
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {bookingQuery?.booked_room || "N/A"}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <div className="flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Rooms
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {property?.rooms_count || "N/A"}
              </span>
            </div>
          )}

          {/* Minimum Booking Period */}
          <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Minimum Period
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {minimumMonths
                ? `${minimumMonths} ${minimumMonths === 1 ? "month" : "months"}`
                : "N/A"}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Period
              </span>
            </div>
    
          <span className="text-xs text-gray-600 dark:text-gray-400 text-right">
            {moment(checkInDate).format("MMM DD")} -{" "}
            {moment(checkOutDate).format("MMM DD, YYYY")}
          </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Duration
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {bookedMonths || "N/A"}{" "}
              {bookedMonths === 1 ? "month" : "months"}
            </span>
          </div>

          {/* Warning if booking is too short */}
          {isBookingTooShort && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">
                    Below Minimum
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {bookedMonths} {bookedMonths === 1 ? "month" : "months"} is
                    less than the {minimumMonths}{" "}
                    {minimumMonths === 1 ? "month" : "months"} minimum.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Guests
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {bookingQuery?.guest_count || "N/A"}
            </span>
          </div>
        </div>

        {/* Inquiry Badge */}
        <div className="mt-4 p-3 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-xs text-center text-blue-900 dark:text-blue-100 font-medium">
            🔍 Property Inquiry
          </p>
        </div>
      </div>
    </div>
  );
};

export default QueryPropertyCard;